import { useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Upload04Icon } from "../../../assets/icons";
import { routes } from "../../../routes/pageRoutes";
import CommonButton from "../../../components/commonButton";
import { exportPendingVerifications } from "../../../redux/slice/companySlice";
import { usePermission } from "../../../hooks/usePermission";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";

const MODULE = "company_verification";

const ListSection = ({ searchTerm, setSearchTerm, isLoading,
    isInitialLoad, cardStatus, data, pagination }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isExporting, setIsExporting] = useState(false);
    const { hasPermission } = usePermission();

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Company / </span>Verification Requests
            </h4>
            <div className="action-buttons-container d-flex align-items-center gap-3">
                {hasPermission(MODULE, "export") && (
                    <CommonButton
                        icon={<Upload04Icon strokeWidth="2.0" />}
                        variant="secondary"
                        title={isExporting ? "Exporting..." : "Export"}
                        type="button"
                        disabled={isExporting}
                        isLoading={isExporting}
                        onClick={async () => {
                            setIsExporting(true);
                            try {
                                await dispatch(exportPendingVerifications({
                                    search: searchTerm,
                                    status: cardStatus,
                                })).unwrap();
                            } catch (err) {
                                console.error("Export failed:", err);
                            } finally {
                                setIsExporting(false);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );

    const actionList = [
        {
            title: "View",
            handler: (item) => {
                navigate(routes.viewVerificationRequests.replace(":id", item.id));
            }
        }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB"); // dd-mm-yyyy
    };

    const columns = [
        {
            id: "company_name",
            header: "NAME",
            cell: (_, item) => (
                <div className="d-flex align-items-center gap-2">
                    <div className="profile-icon">
                        <img src={item.company?.profile?.logo?.url || "/images/AllUser/all-companies.png"} alt="logo" style={{ objectFit: "cover", width: "100%", height: "100%" }} />
                    </div>
                    <span>{item.company?.name || "-"}</span>
                </div>
            )
        },
        {
            id: "owner_name",
            header: "OWNER NAME",
            cell: (_, item) => (
                <div className="d-flex align-items-center gap-2">
                    <div className="profile-icon">
                        <img src={item.company?.creator?.profile?.profile_photo?.url || "/images/AllUser/UserImage.jpg"} alt="owner" style={{ objectFit: "cover", width: "100%", height: "100%" }} />
                    </div>
                    <div className="d-flex flex-column">
                        <span className="text-secondary fw-medium">{item.company?.creator ? `${item.company.creator.first_name} ${item.company.creator.last_name || ""}`.trim() : "-"}</span>
                        <small className="text-muted">{item.company?.creator?.mobile_number || "-"}</small>
                    </div>
                </div>
            )
        },
        {
            id: "mobile_number",
            header: "MOBILE NUMBER",
            cell: (_, item) => item.company?.profile?.mobile_number || "-"
        },
        {
            id: "email",
            header: "EMAIL ADDRESS",
            cell: (_, item) => item.company?.profile?.email || "-"
        },
        {
            id: "gstin",
            header: "GST NUMBER",
            cell: (_, item) => item.business_type === 'gstin' ? item.business_number : "-"
        },
        {
            id: "udyam",
            header: "UDYAM AADHAR",
            cell: (_, item) => item.business_type === 'udyam' ? item.business_number : "-"
        },
        {
            id: "pan",
            header: "PAN NUMBER",
            cell: (_, item) => item.pan_number || "-"
        },
        {
            id: "pan_status",
            header: "PANCARD STATUS",
            className: "text-capitalize",
            cell: (_, item) => item.pan_status || "-"
        },
        {
            id: "business_status",
            header: "BUSINESS DOCUMENT STATUS",
            className: "text-capitalize",
            cell: (_, item) => item.business_status || "-"
        },
        {
            id: "other_documents_status",
            header: "OTHER DOCUMENT STATUS",
            className: "text-capitalize",
            cell: (_, item) => item.other_documents_status || "-"
        },
        {
            id: "payment_status",
            header: "PAYMENT STATUS",
            className: "text-capitalize",
            cell: (_, item) => item.payment_status || "-"
        },
        {
            id: "overall_status",
            header: "OVERALL STATUS",
            className: "text-capitalize",
            cell: (_, item) => item.overall_status || "-"
        },
        {
            id: "created_at",
            header: "UPLOADED DATE",
            cell: (_, item) => formatDate(item.created_at)
        },
        {
            id: "verified_at",
            header: "APPROVED DATE",
            cell: (_, item) => formatDate(item.verified_at)
        }
    ];

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <div className="p-4 d-flex align-items-center justify-content-between gap-4">
                <TableSearch value={searchTerm}
                    placeholder="Search"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                    }} />
            </div>

            <CustomTable
                data={data}
                columns={columns}
                keyField="id"
                actionList={actionList}
                module={MODULE}
                emptyMessage="No verification requests found."
                loading={isLoading && !isInitialLoad}
                rowClick={(item) => {
                    navigate(routes.viewVerificationRequests.replace(":id", item.id));
                }}
                tbody={{ trClass: "cursor-pointer" }}
                pagination={pagination}
            />
        </>
    );
};

export default ListSection;
